import { isLivePhoneCall } from './communications-model';
import { useWidgetDismiss } from "../lib/use-widget-dismiss";
import { PhoneFlyout } from "./Communications";
import { disconnectedPhoneData, type PhoneData, type BookingCaller } from "./communications-model";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { API_URL, apiFetch } from "../lib/api-client";
import { formatDispatchTime, isRunningLate, isSettled, type DispatchItem, type DispatchProposal, type Technician } from "../lib/dispatch";

/*
 * THE BELL. One place for everything waiting on the person signed in, on every
 * screen — the office should not have to be standing on the board to find out
 * a job never got a technician.
 *
 * What lands here is decided by what the role is responsible for, not by what
 * happened. Dispatch is told about the day: nobody assigned, double booked,
 * running late, booked with no time on it. The office manager is told about the
 * money and the pricebook. The owner is told nothing unless it is stopping —
 * something is waiting on their approval — which is the whole point of being
 * the owner.
 *
 * Every row is a door: it opens the thing it is about. Nothing pops up over the
 * screen; an alert waits in here until it is opened, done, or hushed.
 */

export type AlertAudience = "waiting" | "dispatch" | "money" | "pricebook";

type AlertTone = "late" | "unassigned" | "waiting" | "sold" | "money" | "pricebook";

export type WorkAlert = {
  key: string;
  group: string;
  tone: AlertTone;
  urgent: boolean;
  text: string;
  when?: string;
  openLabel?: string;
  open?: () => void;
};

/*
 * Colour carries the kind of alert, the way the board's colours carry a job's
 * state — same tokens, so nothing here invents a hue. Red is late or overdue,
 * amber is a hole in the day, purple is waiting on a person, green is money
 * sold, blue is paperwork.
 */
const TONES: Record<AlertTone, { fill: string; ink: string; icon: ReactNode }> = {
  late: {
    fill: "bg-[var(--st-conflict-wash)]", ink: "text-[var(--st-conflict)]",
    icon: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
  },
  unassigned: {
    fill: "bg-[var(--st-working-wash)]", ink: "text-[var(--st-working)]",
    icon: <><path d="M19 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="11" cy="7" r="4" /><path d="M19 8v6M22 11h-6" /></>,
  },
  waiting: {
    fill: "bg-[var(--st-confirmed-wash)]", ink: "text-[var(--st-confirmed)]",
    icon: <><path d="M9 11l2 2 4-4" /><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M8 2v4M16 2v4" /></>,
  },
  sold: {
    fill: "bg-[var(--st-complete-wash)]", ink: "text-[var(--st-complete)]",
    icon: <><path d="M20 6L9 17l-5-5" /></>,
  },
  money: {
    fill: "bg-[var(--st-conflict-wash)]", ink: "text-[var(--st-conflict)]",
    icon: <><path d="M12 1v22" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></>,
  },
  pricebook: {
    fill: "bg-[var(--st-dispatched-wash)]", ink: "text-[var(--st-dispatched)]",
    icon: <><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></>,
  },
};

function ToneBadge({ tone }: { tone: AlertTone }) {
  const style = TONES[tone];
  return <span className={`grid size-7 shrink-0 place-items-center rounded-lg ${style.fill} ${style.ink}`}>
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{style.icon}</svg>
  </span>;
}

function BellIcon() {
  return <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.7 21a2 2 0 0 1-3.4 0" />
  </svg>;
}

function PhoneIcon() {
  return <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>;
}

/* ---- hushing. An alert nobody can put away is an alert nobody reads. ---- */

const SNOOZE_KEY = "wv:alert-snooze";

function readSnoozed(): Record<string, number> {
  let stored: Record<string, number> = {};
  try { stored = JSON.parse(window.localStorage.getItem(SNOOZE_KEY) || "{}"); } catch { stored = {}; }
  const now = Date.now();
  let changed = false;
  for (const key of Object.keys(stored)) if (!(stored[key] > now)) { delete stored[key]; changed = true; }
  if (changed) try { window.localStorage.setItem(SNOOZE_KEY, JSON.stringify(stored)); } catch { /* private mode */ }
  return stored;
}

function hush(key: string, seconds: number) {
  const stored = readSnoozed();
  stored[key] = Date.now() + seconds * 1000;
  try { window.localStorage.setItem(SNOOZE_KEY, JSON.stringify(stored)); } catch { /* private mode */ }
}

/* ---- what the bell is told about ---- */

type BoardPayload = { appointments: DispatchItem[]; untimedJobs: Array<{ id: string; jobNumber: string; customerName: string; description: string | null }>; technicians: Technician[]; timezone: string };
type CommercialItem = { id: string; displayNumber: string; status: string; totalCents: number; balanceDueCents?: number; dueDate?: string | null; customerName: string; jobId: string };
type CommercialSummary = { importReviewCount: number };
type PriceBookReadiness = { materialsWithoutCost: number; zeroPriceItems: number; uncategorizedItems: number };

export type AlertSources = {
  board: BoardPayload | null;
  proposals: DispatchProposal[];
  invoices: CommercialItem[];
  estimates: CommercialItem[];
  summary: CommercialSummary | null;
  readiness: PriceBookReadiness | null;
};

export type AlertActions = {
  openBoard: () => void;
  openJob: (jobId: string) => void;
  openAccounting: (mode: "invoices" | "estimates") => void;
  openPricebook: () => void;
};

const money = (cents: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(cents / 100);

/**
 * The audiences a role is subscribed to. The owner is deliberately thin: the
 * board and the money have people whose job they are, and an owner buried in
 * their alerts stops reading the one that was actually theirs.
 */
export function audiencesForRole(role: string, permissions: string[]): AlertAudience[] {
  const can = (permission: string) => permissions.includes(permission);
  if (role === "owner") return ["waiting"];
  const audiences: AlertAudience[] = ["waiting"];
  if (can("scheduling:read")) audiences.push("dispatch");
  if (can("commercial:read")) audiences.push("money", "pricebook");
  return audiences;
}

export function buildAlerts(audiences: AlertAudience[], sources: AlertSources, actions: AlertActions, timezone: string): WorkAlert[] {
  const snoozed = readSnoozed();
  const alerts: WorkAlert[] = [];
  const add = (alert: WorkAlert) => { if (!snoozed[alert.key]) alerts.push(alert); };
  const has = (audience: AlertAudience) => audiences.includes(audience);

  if (has("waiting")) {
    for (const proposal of sources.proposals.filter((item) => item.status === "pending")) {
      const { jobId, jobNumber, ownerType, customerName, newStart } = proposal.payload.display;
      add({ key: `proposal:${proposal.id}`, group: "Waiting on you", tone: "waiting", urgent: true,
        text: `The agent wants to ${proposal.changeType === "create_appointment" ? "book" : proposal.changeType === "move_appointment" ? "move" : "reassign"} ${jobId ? '#' + jobNumber : (ownerType ?? '') + ' appointment'} · ${customerName}`,
        when: `${formatDispatchTime(newStart, timezone)} · asked by ${proposal.requestedByName || "the agent"}`,
        openLabel: "Review it", open: actions.openBoard });
    }
  }

  if (has("dispatch") && sources.board) {
    const { appointments, untimedJobs } = sources.board;
    for (const item of appointments.filter((appointment) => isRunningLate(appointment, timezone))) {
      add({ key: `late:${item.id}`, group: "Wrong on the board", tone: "late", urgent: true,
        text: `Running late — ${item.jobId ? '#' + item.jobNumber : item.ownerType + ' appointment'} · ${item.customerName}`,
        when: `window opened ${formatDispatchTime(item.scheduledStart, timezone)} · ${item.technicianNames || "nobody assigned"}`,
        openLabel: item.jobId ? "Open the job" : "Open the board", open: () => item.jobId ? actions.openJob(item.jobId) : actions.openBoard() });
    }
    for (const item of appointments.filter((appointment) => !appointment.technicianIds.length && !isSettled(appointment))) {
      add({ key: `unassigned:${item.id}`, group: "Holes in the day", tone: "unassigned", urgent: true,
        text: `Nobody assigned — ${item.jobId ? '#' + item.jobNumber : item.ownerType + ' appointment'} · ${item.customerName}`,
        when: formatDispatchTime(item.scheduledStart, timezone),
        openLabel: "Open the board", open: actions.openBoard });
    }
    for (const job of untimedJobs) {
      add({ key: `untimed:${job.id}`, group: "Holes in the day", tone: "unassigned", urgent: true,
        text: `Booked with no time on it — #${job.jobNumber} · ${job.customerName}`,
        openLabel: "Open the job", open: () => actions.openJob(job.id) });
    }
  }

  if (has("money")) {
    // Sold work that nobody put on the board. The estimate is accepted and the
    // customer is waiting; until it has a date it is a promise, not a job.
    for (const estimate of sources.estimates.filter((item) => item.status.toLowerCase().includes("accept") || item.status.toLowerCase().includes("approv"))) {
      add({ key: `sold:${estimate.id}`, group: "Sold, needs a date", tone: "sold", urgent: true,
        text: `${estimate.customerName} — ${money(estimate.totalCents)} sold on ${estimate.displayNumber}`,
        openLabel: "Open the job", open: () => actions.openJob(estimate.jobId) });
    }
    const overdue = sources.invoices.filter((item) => (item.balanceDueCents ?? 0) > 0 && item.dueDate && Date.parse(item.dueDate) < Date.now());
    if (overdue.length) {
      const total = overdue.reduce((sum, item) => sum + (item.balanceDueCents ?? 0), 0);
      add({ key: `overdue:${overdue.length}:${total}`, group: "Money", tone: "money", urgent: true,
        text: `${overdue.length} invoice${overdue.length === 1 ? "" : "s"} past due — ${money(total)} outstanding`,
        openLabel: "Open accounting", open: () => actions.openAccounting("invoices") });
    }
    const review = sources.summary?.importReviewCount ?? 0;
    if (review) {
      add({ key: `import-review:${review}`, group: "Money", tone: "pricebook", urgent: false,
        text: `${review} imported invoice${review === 1 ? "" : "s"} still need a look`,
        openLabel: "Open accounting", open: () => actions.openAccounting("invoices") });
    }
  }

  if (has("pricebook") && sources.readiness) {
    const { materialsWithoutCost, zeroPriceItems, uncategorizedItems } = sources.readiness;
    if (materialsWithoutCost) add({ key: `pb-cost:${materialsWithoutCost}`, group: "Pricebook", tone: "pricebook", urgent: false,
      text: `${materialsWithoutCost} material${materialsWithoutCost === 1 ? "" : "s"} priced with no cost behind them`,
      openLabel: "Open the pricebook", open: actions.openPricebook });
    if (zeroPriceItems) add({ key: `pb-zero:${zeroPriceItems}`, group: "Pricebook", tone: "pricebook", urgent: false,
      text: `${zeroPriceItems} item${zeroPriceItems === 1 ? "" : "s"} would go on an invoice at $0`,
      openLabel: "Open the pricebook", open: actions.openPricebook });
    if (uncategorizedItems) add({ key: `pb-cat:${uncategorizedItems}`, group: "Pricebook", tone: "pricebook", urgent: false,
      text: `${uncategorizedItems} item${uncategorizedItems === 1 ? "" : "s"} nobody can find — no category on them`,
      openLabel: "Open the pricebook", open: actions.openPricebook });
  }

  return alerts;
}

async function load<T>(path: string, signal?: AbortSignal): Promise<T | null> {
  try {
    const response = await apiFetch(path, { signal });
    if (!response.ok) return null;
    return (await response.json() as { data: T }).data;
  } catch { return null; }
}


export default function AlertBell({ role, permissions, proposals, timezone, actions, pageBoard, pageReadiness, phoneData: initialPhoneData = disconnectedPhoneData, openCommunications, bookCaller, activeWidget, onWidgetChange, clearComposer, onUnreadTextsChange }: {
  activeWidget?: "texts" | "phone" | "alerts" | null;
  onWidgetChange?: (widget: "texts" | "phone" | "alerts" | null) => void;
  clearComposer?: boolean;
  onUnreadTextsChange?: (count: number) => void;
  phoneData?: PhoneData;
  openCommunications?: () => void;
  bookCaller?: (caller: BookingCaller) => void;
  role: string;
  permissions: string[];
  proposals: DispatchProposal[];
  timezone: string;
  actions: AlertActions;
  /** Today's board, when the page is already showing it. */
  pageBoard?: { date: string; payload: BoardPayload } | null;
  /** Pricebook readiness, when the page has already loaded the pricebook. */
  pageReadiness?: PriceBookReadiness | null;
}) {
  const audiences = useMemo(() => audiencesForRole(role, permissions), [role, permissions]);
  const canSeeTexts = permissions.includes("scheduling:read") && permissions.includes("customers:read");
  const canSeePhone = permissions.includes("scheduling:read") && permissions.includes("customers:read") && permissions.includes("jobs:read");
  const [livePhoneData, setLivePhoneData] = useState<PhoneData | null>(null);
  const phoneData = livePhoneData ?? initialPhoneData;
  useEffect(() => {
    if (!canSeeTexts) return;
    let active = true;
    let reconnectTimer: number | undefined;
    let refreshTimer: number | undefined;
    let callsRefreshTimer: number | undefined;
    let socket: WebSocket | undefined;
    let refreshInFlight = false;
    let refreshQueued = false;
    let callsInFlight = false;
    let callsQueued = false;
    let reconnectDelay = 1_000;
    const refreshPhone = async () => {
      if (refreshInFlight) { refreshQueued = true; return; }
      refreshInFlight = true;
      try {
        const response = await apiFetch('/api/v1/workspace/communications/live-sms?page=0&view=needs_response&includeCalls=false');
        if (!response.ok) return;
        const body = await response.json() as { data: { messaging?: PhoneData['messaging']; hasMore?: boolean; unread?: number; companyPhone: string | null; calls?: PhoneData['calls']; items: Array<{ id: string; customerName: string | null; phone: string; status?: string; lastDirection?: string | null; unread: number; summary: string | null }> } };
        if (active && Array.isArray(body.data.items)) setLivePhoneData(current => ({ unread: body.data.unread, hasMore: body.data.hasMore, messaging: body.data.messaging, companyPhone: body.data.companyPhone, connected: Boolean(body.data.companyPhone), calls: current?.calls ?? initialPhoneData.calls, threads: body.data.items.map(item => ({ id: item.id, customerName: item.customerName, phone: item.phone, status: item.status, lastDirection: item.lastDirection, unread: item.unread, messages: item.summary ? [{ id: `${item.id}:latest`, direction: 'inbound', body: item.summary, createdAt: '' }] : [] })) }));
      } catch { /* The phone flyout remains usable with its existing disconnected state. */ }
      finally {
        refreshInFlight = false;
        if (active && refreshQueued) { refreshQueued = false; void refreshPhone(); }
      }
    };
    const refreshCalls = async () => {
      if (!canSeePhone) return;
      if (callsInFlight) { callsQueued = true; return; }
      callsInFlight = true;
      try {
        const response = await apiFetch('/api/v1/workspace/communications/live-calls');
        if (!response.ok) return;
        const body = await response.json() as { data: { items: PhoneData['calls'] } };
        if (active && Array.isArray(body.data.items)) setLivePhoneData(current => ({ ...(current ?? initialPhoneData), calls: body.data.items }));
      } catch { /* Retain the last known calls until the next event or safety poll. */ }
      finally {
        callsInFlight = false;
        if (active && callsQueued) { callsQueued = false; void refreshCalls(); }
      }
    };
    const scheduleCallsRefresh = () => {
      if (callsRefreshTimer !== undefined) return;
      callsRefreshTimer = window.setTimeout(() => { callsRefreshTimer = undefined; void refreshCalls(); }, 400);
    };
    void refreshPhone();
    void refreshCalls();
    // Twilio commonly emits several lifecycle callbacks for one call. Coalesce
    // their WebSocket notifications into one refresh, without losing a change
    // that lands while that request is running.
    const refreshTexts = () => {
      if (refreshTimer !== undefined) return;
      refreshTimer = window.setTimeout(() => { refreshTimer = undefined; void refreshPhone(); }, 400);
    };
    const connectCallEvents = () => {
      if (!canSeePhone || !active || typeof WebSocket === 'undefined') return;
      try {
        socket = new WebSocket(`${API_URL.replace(/^http/, 'ws')}/api/v1/workspace/communications/live-events`);
      } catch {
        reconnectTimer = window.setTimeout(connectCallEvents, reconnectDelay);
        reconnectDelay = Math.min(reconnectDelay * 2, 30_000);
        return;
      }
      socket.onopen = () => { reconnectDelay = 1_000; };
      socket.onmessage = scheduleCallsRefresh;
      socket.onclose = () => {
        if (!active) return;
        reconnectTimer = window.setTimeout(connectCallEvents, reconnectDelay);
        reconnectDelay = Math.min(reconnectDelay * 2, 30_000);
      };
      socket.onerror = () => socket?.close();
    };
    connectCallEvents();
    window.addEventListener("workview:texts-changed", refreshTexts);
    // Background tabs and slow requests must not accumulate poll retries.
    const timer = window.setInterval(() => { if (!document.hidden && !refreshInFlight) refreshTexts(); }, 10_000);
    const callsTimer = window.setInterval(() => { if (!document.hidden && !callsInFlight) void refreshCalls(); }, 60_000);
    const onVisibilityChange = () => { if (!document.hidden) { refreshTexts(); scheduleCallsRefresh(); } };
    document.addEventListener("visibilitychange", onVisibilityChange);
    const refreshChangedCalls = scheduleCallsRefresh;
    window.addEventListener('workview:calls-changed', refreshChangedCalls);
    return () => { active = false; socket?.close(); if (reconnectTimer !== undefined) window.clearTimeout(reconnectTimer); if (refreshTimer !== undefined) window.clearTimeout(refreshTimer); if (callsRefreshTimer !== undefined) window.clearTimeout(callsRefreshTimer); window.clearInterval(timer); window.clearInterval(callsTimer); document.removeEventListener("visibilitychange", onVisibilityChange); window.removeEventListener("workview:texts-changed", refreshTexts); window.removeEventListener('workview:calls-changed', refreshChangedCalls); };
  }, [canSeeTexts, canSeePhone]);
  const phoneCount = phoneData.calls.filter(isLivePhoneCall).length;
  const ringingCount = phoneData.calls.filter(call => isLivePhoneCall(call) && call.state === "ringing" && !call.assignedToUserId).length;
  const ringing = phoneData.connected && ringingCount > 0;
  const unreadTexts = phoneData.unread ?? phoneData.threads.reduce((sum, thread) => sum + thread.unread, 0);
  useEffect(() => { onUnreadTextsChange?.(unreadTexts); }, [unreadTexts, onUnreadTextsChange]);
  const [localWidget, setLocalWidget] = useState<"texts" | "phone" | "alerts" | null>(null);
  const widget = activeWidget === undefined ? localWidget : activeWidget;
  const setWidget = onWidgetChange ?? setLocalWidget;
  const open = widget === "alerts";
  const phoneOpen = widget === "phone";
  const alertsPanel = useRef<HTMLElement>(null);
  useWidgetDismiss(alertsPanel, open, '[data-workspace-widget-trigger="alerts"]', () => setWidget(null));
  const [hushed, setHushed] = useState(0);
  const [sources, setSources] = useState<AlertSources>({ board: null, proposals: [], invoices: [], estimates: [], summary: null, readiness: null });

  // Commercial and Pricebook alert inputs are a compact server projection.
  // Keep them through bell opens; successful local mutations invalidate them.
  const alertSourcesOnce = useRef<Pick<AlertSources, "invoices" | "estimates" | "summary" | "readiness"> | undefined>(undefined);
  const pageSources = useRef({ pageBoard, pageReadiness });
  pageSources.current = { pageBoard, pageReadiness };
  const pendingRefresh = useRef<AbortController | null>(null);
  const wantsDispatch = audiences.includes("dispatch");
  const wantsMoney = audiences.includes("money");
  const wantsPricebook = audiences.includes("pricebook");
  const today = new Intl.DateTimeFormat("en-CA", { timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
  const refresh = useCallback(async () => {
    pendingRefresh.current?.abort();
    const controller = new AbortController();
    pendingRefresh.current = controller;
    const { signal } = controller;
    const { pageBoard, pageReadiness } = pageSources.current;
    const wantsBoard = wantsDispatch;
    const haveBoard = pageBoard && pageBoard.date === today ? pageBoard.payload : null;
    const [board, alertSources] = await Promise.all([
      haveBoard
        ? Promise.resolve(haveBoard)
        : wantsBoard
          ? load<BoardPayload>(`/api/v1/workspace/dispatch/board?date=${today}`, signal)
          : Promise.resolve(null),
      (wantsMoney || wantsPricebook) && alertSourcesOnce.current === undefined
        ? load<Pick<AlertSources, "invoices" | "estimates" | "summary" | "readiness">>("/api/v1/workspace/alerts", signal)
        : Promise.resolve(null),
    ]);
    if (signal.aborted) return;
    if (alertSourcesOnce.current === undefined && alertSources) alertSourcesOnce.current = alertSources;
    setSources({
      board,
      proposals: [],
      invoices: alertSourcesOnce.current?.invoices ?? [],
      estimates: alertSourcesOnce.current?.estimates ?? [],
      summary: alertSourcesOnce.current?.summary ?? null,
      readiness: pageReadiness ?? alertSourcesOnce.current?.readiness ?? null,
    });
  }, [wantsDispatch, wantsMoney, wantsPricebook, today]);

  // Parent renders and bell opens reuse the current projection.
  useEffect(() => {
    void refresh();
    return () => pendingRefresh.current?.abort();
  }, [refresh]);
  useEffect(() => {
    const invalidate = () => {
      alertSourcesOnce.current = undefined;
      void refresh();
    };
    window.addEventListener("workview:alerts-changed", invalidate);
    return () => window.removeEventListener("workview:alerts-changed", invalidate);
  }, [refresh]);
  const currentBoard = pageBoard?.date === today ? pageBoard.payload : sources.board;

  // The page already holds the proposals and decides them; the bell reads that
  // rather than fetching a second, staler copy of the same list.
  const alerts = useMemo(
    () => buildAlerts(audiences, { ...sources, board: currentBoard, readiness: pageReadiness ?? sources.readiness, proposals }, actions, timezone),
    // `hushed` is a tick: hushing writes to localStorage, which the build reads.
    [audiences, sources, currentBoard, pageReadiness, proposals, actions, timezone, hushed],
  );
  const count = alerts.filter((alert) => alert.urgent).length || alerts.length;
  const groups = useMemo(() => {
    const ordered: Array<{ name: string; items: WorkAlert[] }> = [];
    for (const alert of alerts) {
      const group = ordered.find((entry) => entry.name === alert.group);
      if (group) group.items.push(alert); else ordered.push({ name: alert.group, items: [alert] });
    }
    return ordered;
  }, [alerts]);

  const put = (key: string, seconds: number) => { hush(key, seconds); setHushed((tick) => tick + 1); };

  return <>


    {open && <aside ref={alertsPanel} aria-label="Alerts" className="fixed bottom-24 right-6 z-50 flex max-h-[70vh] w-[min(400px,92vw)] flex-col overflow-hidden rounded-2xl border border-[var(--line)] bg-[var(--paper)] shadow-[0_16px_48px_rgba(17,17,17,.18)]">
      <header className="flex items-center justify-between border-b border-[var(--line)] px-4 py-3">
        <p className="text-xs font-semibold uppercase tracking-[.14em] text-[var(--ink-soft)]">Needs you</p>
        <button type="button" onClick={() => setWidget(null)} className="text-xs font-semibold text-[var(--accent)]">close</button>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {groups.map((group) => <section key={group.name}>
          <p className="sticky top-0 z-10 bg-[var(--wash)] px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-[var(--ink-soft)]">{group.name}</p>
          {group.items.map((alert) => <div key={alert.key} className="flex gap-2.5 border-b border-[var(--line)] px-4 py-3 last:border-0">
            <ToneBadge tone={alert.tone} />
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-semibold leading-5 text-[var(--ink)]">{alert.text}</p>
              {alert.when && <p className="mt-0.5 text-[11.5px] text-[var(--ink-soft)]">{alert.when}</p>}
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                {alert.open && <button type="button" onClick={() => { setWidget(null); alert.open?.(); }} className="rounded-full bg-[var(--accent)] px-2.5 py-1 text-[11px] font-semibold text-white transition hover:brightness-110">{alert.openLabel || "Open"}</button>}
                <button type="button" onClick={() => put(alert.key, 3600)} title="Hide for an hour" className="rounded-full border border-[var(--line)] px-2.5 py-1 text-[11px] font-semibold text-[var(--ink-soft)] transition hover:bg-[var(--wash)]">1h</button>
                <button type="button" onClick={() => put(alert.key, 86400)} title="Hide for a day" className="rounded-full border border-[var(--line)] px-2.5 py-1 text-[11px] font-semibold text-[var(--ink-soft)] transition hover:bg-[var(--wash)]">1d</button>
                <button type="button" onClick={() => put(alert.key, 2592000)} title="Not needed" className="rounded-full border border-[var(--line)] px-2.5 py-1 text-[11px] font-semibold text-[var(--ink-faint)] transition hover:bg-[var(--wash)]">Dismiss</button>
              </div>
            </div>
          </div>)}
        </section>)}
        {!groups.length && <p className="px-4 py-12 text-center text-sm text-[var(--ink-soft)]">All clear — nothing is waiting on you.</p>}
      </div>
    </aside>}

    {createPortal(<>
      {phoneOpen && <PhoneFlyout canCancelJob={permissions.includes("jobs:write")} canClaimCalls={permissions.includes("scheduling:write")} canManageSpam={permissions.includes("scheduling:write")} data={phoneData} timezone={timezone} onClose={() => setWidget(null)}
        onOpenBoard={() => { setWidget(null); openCommunications?.(); }}
        onBookJob={bookCaller && permissions.includes("scheduling:write") ? caller => { setWidget(null); bookCaller(caller); } : undefined} />}

      <div className={`fixed right-6 z-[60] flex flex-col items-center gap-2.5 ${clearComposer ? "bottom-28" : "bottom-6"}`}>
      {canSeePhone && <button type="button" onPointerDown={(event) => event.stopPropagation()} onClick={(event) => { event.stopPropagation(); setWidget(phoneOpen ? null : "phone"); }}
        data-workspace-widget-trigger="phone" aria-label={ringing ? `${ringingCount} ringing · ${phoneCount} active ${phoneCount === 1 ? "call" : "calls"} — open phone` : "Open phone calls"} title="Phone calls" aria-expanded={phoneOpen} aria-haspopup="dialog"
        className={`comms-existing-phone ${ringing ? "is-ringing" : ""} relative grid h-11 w-11 place-items-center rounded-full border shadow-lg transition ${clearComposer ? "border-red-600 bg-red-600 text-white hover:bg-red-700" : phoneOpen ? "border-[var(--accent)] bg-[var(--accent)] text-white" : "border-[var(--line)] bg-[var(--paper)] text-[var(--ink-soft)] hover:bg-[var(--accent-wash)] hover:text-[var(--accent)]"}`}>
        {ringing && <span className="comms-ringing-label" role="status">{ringingCount} ringing · {phoneCount} active {phoneCount === 1 ? "call" : "calls"}</span>}
        <PhoneIcon />
        {phoneCount > 0 && <span className="absolute -right-1 -top-1 rounded-full bg-[var(--accent)] px-1.5 text-[10px] text-white">{phoneCount}</span>}
      </button>}
      {(!clearComposer || count > 0) && <button type="button" onClick={() => { if (!open) void refresh(); setWidget(open ? null : "alerts"); }}
        data-workspace-widget-trigger="alerts" aria-label={count ? `${count} alerts` : "No alerts"} title={count ? `${count} need attention` : "Nothing needs attention"}
        className={`relative grid h-12 w-12 place-items-center rounded-full border shadow-lg transition ${open ? "border-[var(--accent)] bg-[var(--accent)] text-white" : "border-[var(--line)] bg-[var(--paper)] text-[var(--ink-soft)] hover:bg-[var(--accent-wash)] hover:text-[var(--accent)]"}`}>
        <BellIcon />
        {count > 0 && <span className="absolute -right-0.5 -top-0.5 grid min-w-[18px] place-items-center rounded-full bg-[var(--st-alert)] px-1 text-[10px] font-semibold leading-4 text-white">{count > 9 ? "9+" : count}</span>}
      </button>}
      </div>
    </>, document.body)}
  </>;
}
